import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminRegistrationClosed.css";

// ─── Grid constants ───────────────────────────────────────────────────────────
const COLS = 24;
const ROWS = 14;
const TOTAL = COLS * ROWS;

// Phase durations (ms)
const PHASE_DURATIONS = {
  scatter: 2400,
  flash: 600,
  sweep: 1800,
  burst: 1600,
  hold: 3200,
};

// Build initial dead grid
const makeGrid = () =>
  Array.from({ length: TOTAL }, (_, i) => ({
    id: i,
    col: i % COLS,
    row: Math.floor(i / COLS),
    brightness: 0,   // 0–1
    color: "gold",   // "gold" | "white"
    visible: false,
  }));

// Euclidean distance from center
const distFromCenter = (col, row) => {
  const cx = (COLS - 1) / 2;
  const cy = (ROWS - 1) / 2;
  return Math.sqrt((col - cx) ** 2 + (row - cy) ** 2);
};

const MAX_DIST = distFromCenter(0, 0);

export default function AdminRegistrationClosed() {
  const navigate = useNavigate();
  const [grid, setGrid] = useState(makeGrid);
  const [phase, setPhase] = useState("hold");
  const [showTitle, setShowTitle] = useState(false);
  const frameRef = useRef(null);
  const phaseRef = useRef("hold");
  const phaseStartRef = useRef(Date.now());
  const animRef = useRef(null);
  const mountedRef = useRef(true);

  // ─── Phase runner ────────────────────────────────────────────────────────
  const runPhase = useCallback((phaseName) => {
    if (!mountedRef.current) return;
    phaseRef.current = phaseName;
    phaseStartRef.current = Date.now();
    setPhase(phaseName);
    if (phaseName !== "hold") setShowTitle(false);
  }, []);

  const scheduleNext = useCallback(
    (phaseName, delay) => {
      if (animRef.current) clearTimeout(animRef.current);
      animRef.current = setTimeout(() => {
        if (mountedRef.current) runPhase(phaseName);
      }, delay);
    },
    [runPhase]
  );

  // ─── Per-phase grid logic ─────────────────────────────────────────────────
  useEffect(() => {
    if (phase === "scatter") {
      // Randomly illuminate ~35% of cells, staggered
      const chosen = new Set();
      while (chosen.size < Math.floor(TOTAL * 0.35)) {
        chosen.add(Math.floor(Math.random() * TOTAL));
      }
      const delays = [];
      chosen.forEach((idx) => {
        delays.push(
          setTimeout(() => {
            if (!mountedRef.current) return;
            setGrid((prev) =>
              prev.map((cell) =>
                cell.id === idx
                  ? {
                      ...cell,
                      visible: true,
                      brightness: 0.4 + Math.random() * 0.5,
                      color: Math.random() > 0.4 ? "gold" : "white",
                    }
                  : cell
              )
            );
          }, Math.random() * PHASE_DURATIONS.scatter * 0.8)
        );
      });
      scheduleNext("flash", PHASE_DURATIONS.scatter);
      return () => delays.forEach(clearTimeout);
    }

    if (phase === "flash") {
      // Entire grid → white flash → warm gold
      setGrid((prev) =>
        prev.map((cell) => ({
          ...cell,
          visible: true,
          brightness: 1,
          color: "white",
        }))
      );
      const t = setTimeout(() => {
        if (!mountedRef.current) return;
        setGrid((prev) =>
          prev.map((cell) => ({ ...cell, color: "gold", brightness: 0.6 }))
        );
      }, 200);
      scheduleNext("sweep", PHASE_DURATIONS.flash);
      return () => clearTimeout(t);
    }

    if (phase === "sweep") {
      // Wave sweeps left → right column by column
      const COL_DELAY = PHASE_DURATIONS.sweep / COLS;
      const delays = [];
      for (let c = 0; c < COLS; c++) {
        delays.push(
          setTimeout(() => {
            if (!mountedRef.current) return;
            setGrid((prev) =>
              prev.map((cell) => {
                if (cell.col === c)
                  return { ...cell, brightness: 1, color: "white", visible: true };
                if (cell.col < c)
                  return { ...cell, brightness: 0.15 + Math.random() * 0.1, color: "gold" };
                return cell;
              })
            );
          }, c * COL_DELAY)
        );
      }
      scheduleNext("burst", PHASE_DURATIONS.sweep);
      return () => delays.forEach(clearTimeout);
    }

    if (phase === "burst") {
      // Cells animate radially from center, brightest at center
      const delays = [];
      for (let i = 0; i < TOTAL; i++) {
        const col = i % COLS;
        const row = Math.floor(i / COLS);
        const d = distFromCenter(col, row);
        const delay = (d / MAX_DIST) * PHASE_DURATIONS.burst * 0.6;
        delays.push(
          setTimeout(() => {
            if (!mountedRef.current) return;
            setGrid((prev) =>
              prev.map((cell) =>
                cell.id === i
                  ? {
                      ...cell,
                      visible: true,
                      brightness: 1 - (d / MAX_DIST) * 0.7,
                      color: d < MAX_DIST * 0.4 ? "white" : "gold",
                    }
                  : cell
              )
            );
          }, delay)
        );
      }
      scheduleNext("hold", PHASE_DURATIONS.burst);
      return () => delays.forEach(clearTimeout);
    }

    if (phase === "hold") {
      // Dim the grid to a gentle resting glow
      setGrid((prev) =>
        prev.map((cell) => ({
          ...cell,
          visible: Math.random() > 0.55,
          brightness: 0.08 + Math.random() * 0.12,
          color: "gold",
        }))
      );
      // Fade in title after short pause
      const titleT = setTimeout(() => {
        if (mountedRef.current) setShowTitle(true);
      }, 400);
      scheduleNext("scatter", PHASE_DURATIONS.hold);
      return () => clearTimeout(titleT);
    }
  }, [phase, scheduleNext]);

  // ─── Cleanup ──────────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    runPhase("scatter");
    return () => {
      mountedRef.current = false;
      if (animRef.current) clearTimeout(animRef.current);
    };
  }, [runPhase]);

  return (
    <div className="arc-root">
      {/* ── Left Column ── */}
      <div className="arc-left">
        <div className="arc-glass-card">
          <div className="arc-card-eyebrow">AAEmployee Platform</div>
          <h2 className="arc-card-title">Existing Users</h2>
          <p className="arc-card-subtitle">Access your organization dashboard.</p>
          <button
            className="arc-login-btn"
            onClick={() => navigate("/admin/login")}
            aria-label="Login to admin dashboard"
          >
            Login
          </button>
          <div className="arc-card-divider" />
          <p className="arc-card-note">
            New to AAEmployee?{" "}
            <span className="arc-note-accent">See below for availability.</span>
          </p>
        </div>
      </div>

      {/* ── Right Column ── */}
      <div className="arc-right">
        {/* Decorative frame */}
        <div className="arc-frame">
          <span className="arc-corner arc-corner--tl" />
          <span className="arc-corner arc-corner--tr" />
          <span className="arc-corner arc-corner--bl" />
          <span className="arc-corner arc-corner--br" />
        </div>

        {/* Scanline overlay */}
        <div className="arc-scanlines" aria-hidden="true" />

        {/* Vignette */}
        <div className="arc-vignette" aria-hidden="true" />

        {/* Animated grid */}
        <div
          className="arc-grid"
          aria-hidden="true"
          style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
        >
          {grid.map((cell) => (
            <div
              key={cell.id}
              className={`arc-cell arc-cell--${cell.color}`}
              style={{
                opacity: cell.visible ? cell.brightness : 0,
              }}
            />
          ))}
        </div>

        {/* Center text (appears during Hold) */}
        <div className={`arc-center-content ${showTitle ? "arc-center-content--visible" : ""}`}>
          <div className="arc-suits-title">
            <span className="arc-suits-line1">AAEmployee</span>
            <div className="arc-suits-rule" />
            <span className="arc-suits-line2">Invitation Only</span>
          </div>
        </div>

        {/* Bottom notice */}
        <div className="arc-notice">
          <div className="arc-notice-rule" />
          <p className="arc-notice-heading">
            Organization registration is temporarily unavailable.
          </p>
          <p className="arc-notice-body">
            AAEmployee is currently accepting new organizations by invitation only
            while we complete platform improvements.
          </p>
          <p className="arc-notice-contact">
            For inquiries, contact{" "}
            <a
              href="mailto:info@ouralife.net"
              className="arc-notice-link"
            >
              info@ouralife.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
